import React, { useMemo, useRef, useState } from 'react';
import { FormGroup, Label } from 'reactstrap';
import { Formik, Form, Field } from 'formik';
import Rating from '../../../components/common/Rating';
import AddIcon from '../../../assets/images/addIcon.svg';
import { ButtonWithLoadingIcon } from '../../../components/common/ButtonWithLoadingIcon';
import * as Yup from 'yup';
import { LoadingContainer } from '../../../components/common/LoadingContainer';
import { LinkButton } from '../../../components/common/LinkButton';

const formValidations = {
  review_text: Yup.string()
    .max(501, 'you can enter maximum 500 character')
    .required('Description is required'),
  review_title: Yup.string().required('Review title is required'),
};

const FormRowScaffold = ({
  label,
  id,
  showError,
  error,
  children,
  fieldDescription = '',
  ...other
}) => {
  return (
    <FormGroup>
      <Label for={id} {...other} style={{ fontFamily: 'Brown Bold' }}>
        {label}
      </Label>
      <div style={{ flexDirection: 'column', display: 'flex' }}>
        {children}
        {showError && (
          <div
            className='invalid-feedback d-block'
            style={{ fontFamily: 'Brown' }}
          >
            {error}
          </div>
        )}

        {fieldDescription && (
          <small className='text-muted mb-2 mt-2 d-block'>
            {fieldDescription}
          </small>
        )}
      </div>
    </FormGroup>
  );
};

const CommonInputFormRow = ({
  label,
  name,
  id,
  type,
  showError,
  error,
  ...otherProps
}) => (
  <FormRowScaffold label={label} id={id} showError={showError} error={error}>
    <Field
      type={type}
      name={name}
      id={id}
      className='form-control'
      {...otherProps}
    />
  </FormRowScaffold>
);

export const ReviewModalStep3 = ({
  productData,
  onClose,
  inputRef,
  onFileChange,
  reviewImage,
  onPressButton,
  onPrevious,
  submittingReview,
  isUploadImage,
  reviewTextValue,
  reviewTitleValue,
  ratingValue,
}) => {
  const formikRef = useRef();

  const [rating, setRating] = useState(
    formikRef?.current?.values?.rating || ratingValue || 0
  );
  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [ratingError, setRatingError] = useState(null);

  const initialValues = {
    review_text:
      productData.review_text ||
      formikRef?.current?.values?.review_text ||
      reviewTextValue ||
      '',
    review_title:
      productData.review_title ||
      formikRef?.current?.values?.review_title ||
      reviewTitleValue ||
      '',
  };

  const getProductValidationSchema = () => {
    return Yup.object().shape({
      ...formValidations,
    });
  };

  const reviewSchema = useMemo(() => getProductValidationSchema(), []);

  const renderButtons = () => {
    return (
      <div className={'review-button'} style={{ border: 'none' }}>
        <ButtonWithLoadingIcon
          style={{
            alignSelf: 'flex-start',
            width: '100%',
            marginBottom: 10,
            backgroundColor:
              submittingReview || isUploadImage ? '#EFEB75' : null,
            borderColor: submittingReview || isUploadImage ? '#2E2B2B' : null,
            color: submittingReview || isUploadImage ? '#2E2B2B' : null,
          }}
          loading={submittingReview}
          type='submit'
          color={'primary'}
          onClick={() => {
            if (rating <= 0) {
              setRatingError('Please give rating');
            }
          }}
          disabled={submittingReview || isUploadImage}
        >
          Submit
        </ButtonWithLoadingIcon>
        <LinkButton
          className={'mt-3'}
          style={{
            marginBottom: 10,
            textAlign: 'center',
            width: '100%',
            height: 55,
            cursor: 'pointer',
          }}
          onClick={() => onPrevious({ ...formikRef?.current?.values, rating })}
        >
          Previous
        </LinkButton>
      </div>
    );
  };
  const handleSubmit = () => {
    if (rating <= 0) {
      setRatingError('Please give rating');
    } else {
      setRatingError(null);
    }
    if (formikRef && formikRef.current && formikRef.current.values) {
      if (
        formikRef.current.values.review_text === '' ||
        formikRef.current.values.review_title === '' ||
        !rating
      ) {
        return;
      } else {
        setErrorText(null);
        const data = {
          ...formikRef.current.values,
          rating,
          reviewImage,
        };
        onPressButton(data);
      }
    } else {
      onPressButton();
    }
  };

  const isFormValid = (errors, touched) => {
    setIsError(
      (errors.review_text && touched.review_text) ||
        (errors.review_title && touched.review_title)
    );
  };

  return (
    <>
      <div
        style={{
          paddingLeft: '2.5rem',
          paddingRight: '2.5rem',
          paddingTop: 25,
        }}
      >
        <div
          className={`basic-detail-item vertical`}
          style={{ marginBottom: 25 }}
        >
          <Label style={{ fontFamily: 'Brown Bold', fontSize: '1rem' }}>
            How would you rate this item overall?
          </Label>
          <div className='basic-detail-data mb-2'>
            <Rating
              total={5}
              rating={rating}
              onRate={(value) => {
                setRating(value.rating);
                if (value.rating > 0) {
                  setRatingError(null);
                }
              }}
            />
          </div>
          {ratingError ? (
            <span
              style={{
                fontSize: '0.76rem',
                color: '#F96E4E',
                fontFamily: 'Brown',
                width: '100%',
              }}
            >
              {ratingError}
            </span>
          ) : null}
        </div>
      </div>

      <Formik
        enableReinitialize={true}
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={reviewSchema}
        autocomplete="off"
      >
        {({
          values,
          errors,
          isSubmitting,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => {
          const isError = isFormValid(errors, touched);
          return (
            <Form className='mb-4'>
              <div
                style={{
                  paddingLeft: '2.5rem',
                  paddingRight: '2.5rem',
                }}
              >
                <div style={{ marginBottom: 30 }}>
                  <CommonInputFormRow
                    label='Give your review a title'
                    type='text'
                    name='review_title'
                    id='product-review-title'
                    placeholder={'Type a short and descriptive title here'}
                    showError={errors.review_title && touched.review_title}
                    error={errors.review_title}
                    isInvalid={errors.review_title && touched.review_title}
                    autocomplete={"off"}
                  />
                </div>

                <div style={{ marginBottom: 30 }}>
                  <CommonInputFormRow
                    label='Write your review'
                    type='text'
                    name='review_text'
                    id='review-description'
                    showError={errors.review_text && touched.review_text}
                    error={errors.review_text}
                    component='textarea'
                    placeholder={`Kindly give us all the deets! How did you style it? What did you like or dislike about it?`}
                    rows={4}
                    isInvalid={errors.review_text && touched.review_text}
                  />
                </div>

                <div style={{ marginBottom: 30 }}>
                  <Label
                    for={'uploadPhoto'}
                    style={{ fontFamily: 'Brown Bold' }}
                  >
                    Upload a photo
                  </Label>

                  {isUploadImage ? (
                    <LoadingContainer
                      style={{
                        backgroundColor: 'rgb(244,241,236)',
                        height: 135,
                        width: 100,
                      }}
                    />
                  ) : reviewImage ? (
                    <div
                      style={{
                        backgroundColor: 'rgb(244,241,236)',
                        height: 135,
                        width: 100,
                      }}
                      className='add-image-wrapper'
                    >
                      <img
                        src={reviewImage}
                        alt=''
                        resizeMode={'contain'}
                        style={{ height: '100%', width: '100%' }}
                        loading={'lazy'}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        height: 135,
                        width: 100,
                      }}
                      className='add-image-wrapper'
                      onClick={(e) => {
                        if (inputRef) inputRef.current.click();
                      }}
                    >
                      <img
                        src={AddIcon}
                        alt=''
                        style={{ height: 22, width: 22 }}
                      />

                      <input
                        ref={inputRef}
                        name='file_uploads'
                        type='file'
                        className='cusInput'
                        id='file_uploads'
                        accept='image/*'
                        multiple
                        value=''
                        hidden
                        onChange={onFileChange}
                      />
                    </div>
                  )}
                </div>
                {isError ? (
                  <div style={{ display: 'flex' }}>
                    <span
                      style={{
                        fontSize: '1rem',
                        color: '#F96E4E',
                        fontFamily: 'Brown',
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      {'Please fix the errors above'}
                    </span>
                  </div>
                ) : null}
              </div>

              {renderButtons()}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
